import { FC } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { renderTextWithLineBreaks } from '@/utils/stringUtils';

/**
 * Props for `AiDebtEbookOverview`.
 */
export type AiDebtEbookOverviewProps =
  SliceComponentProps<Content.AiDebtEbookOverviewSlice>;

/**
 * Green check circle icon matching the Figma design.
 */
const CheckCircleIcon = () => (
  <svg
    width="43"
    height="43"
    viewBox="0 0 43 43"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="shrink-0"
  >
    <circle cx="21.5" cy="21.5" r="21.5" fill="#03BF9E" />
    <path
      d="M13 21.5L19 27.5L31 15.5"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Component for "AI Debt Ebook Overview" Slices.
 */
const AiDebtEbookOverview: FC<AiDebtEbookOverviewProps> = ({ slice }) => {
  const { title, description, learning_section_title, learning_objectives } =
    slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full"
    >
      <div className="mx-auto flex max-w-[1380px] flex-col gap-10 px-5 py-16 md:flex-row md:gap-[60px] md:px-[100px] md:py-[80px]">
        {/* ── Left column: title + description ── */}
        <div className="flex w-full flex-col gap-6 text-[#041118] md:w-[590px] md:shrink-0">
          {isFilled.richText(title) && (
            <h2 className="font-inter text-[32px] font-medium leading-[1.2] md:text-[42px]">
              {renderTextWithLineBreaks(asText(title))}
            </h2>
          )}

          {isFilled.richText(description) && (
            <div className="font-inter text-[16px] font-normal leading-[1.5] text-[#041118] [&>p+p]:mt-6 md:text-[18px]">
              <PrismicRichText field={description} />
            </div>
          )}
        </div>

        {/* ── Right column: learning objectives ── */}
        <div className="flex flex-1 flex-col gap-6">
          {/* Section heading */}
          {learning_section_title && (
            <p className="font-inter text-[20px] font-semibold leading-[1.2] text-[#03BF9E] md:text-[22px]">
              {learning_section_title}
            </p>
          )}

          {/* Objectives list */}
          {learning_objectives && learning_objectives.length > 0 && (
            <div className="flex flex-col gap-6">
              {learning_objectives.map((item, idx) => (
                <div key={idx} className="relative flex gap-2 items-start">
                  {/* Vertical connector line (except on last item) */}
                  {idx < learning_objectives.length - 1 && (
                    <div className="absolute left-[21px] top-[43px] h-[calc(100%+0px)] w-px bg-[#E0E0E0]" />
                  )}

                  <CheckCircleIcon />

                  <div className="flex flex-1 flex-col gap-2 text-[#041118]">
                    {item.objective_title && (
                      <p className="font-inter text-[20px] font-semibold leading-[1.4] md:text-[26px]">
                        {item.objective_title}
                      </p>
                    )}
                    {isFilled.richText(item.objective_description) && (
                      <div className="font-inter text-[16px] font-normal leading-[1.4] md:text-[18px]">
                        <PrismicRichText field={item.objective_description} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AiDebtEbookOverview;
